import {
  BoundSelectField,
  Button,
  Css,
  FormLines,
  ModalBody,
  ModalFooter,
  ModalHeader,
  useComputed,
  useModal,
} from "@homebound/beam";
import { ObjectConfig, required, useFormState } from "@homebound/form-state";
import { useMemo } from "react";
import { ReadyPlanType, SaveReadyPlanLocationInput } from "src/generated/graphqlTypes";
import { PlanPackageLocationsV2Query } from "src/routes/libraries/plan-package-v2/components/LocationsTab.generated";
import {
  LevelGroup,
  SaveRpls,
  levelSortKey,
  maxRoomNumber,
  nextRoomNumber,
  roomNumberPrefix,
  usableNewLevels,
} from "src/routes/libraries/plan-package-v2/components/locations/locationUtils";

type AddLocationModalProps = {
  planPackageId: string;
  planType: ReadyPlanType;
  /** The plan's in-use levels, each with its rooms (see `groupRoomLocations`) */
  groups: LevelGroup[];
  levels: PlanPackageLocationsV2Query["levels"];
  roomTypes: PlanPackageLocationsV2Query["roomTypes"];
  saveRpls: SaveRpls;
};

export function AddLocationModal(props: AddLocationModalProps) {
  const { planPackageId, planType, groups, levels, roomTypes, saveRpls } = props;
  const { closeModal } = useModal();
  const formState = useFormState({ config: addLocationConfig, init: { input: {} } });
  // Existing levels (to add rooms to) plus the addable new ones (next "Level N" / Basement / Roof, or ADU for ADU plans)
  const levelOptions = useMemo(
    () =>
      [...groups.map((group) => group.location), ...usableNewLevels(levels, groups, planType)].sortBy((l) =>
        levelSortKey(l.name),
      ),
    [groups, levels, planType],
  );

  const disabledReason = useComputed(() => {
    const { levelId, roomTypeId } = formState.value;
    if (!levelId) return "Select a level";
    const group = groups.find((g) => g.location.id === levelId);
    const levelName = levelOptions.find((l) => l.id === levelId)!.name;
    if (group && !roomTypeId) return "Select a room to add to this level";
    if (roomTypeId && roomNumberPrefix(levelName) === undefined) return `${levelName} cannot have rooms`;
    if (roomTypeId && nextRoomNumber(levelName, groups) > maxRoomNumber) return `${levelName} has no room numbers left`;
    return false;
  }, [formState, groups, levelOptions]);

  async function onSave() {
    if (!formState.canSave()) return;
    const { levelId, roomTypeId } = formState.value;
    const group = groups.find((g) => g.location.id === levelId);
    const levelName = levelOptions.find((l) => l.id === levelId)!.name;
    const inputs: SaveReadyPlanLocationInput[] = [
      // A level not in use yet needs its own level RPL created
      ...(!group ? [{ readyPlanId: planPackageId, locationId: levelId }] : []),
      ...(roomTypeId
        ? [{ readyPlanId: planPackageId, roomTypeId, levelId, roomNumber: nextRoomNumber(levelName, groups) }]
        : []),
    ];
    await saveRpls(inputs, { membershipChanged: true });
    closeModal();
  }

  return (
    <>
      <ModalHeader>Add a Location</ModalHeader>
      <ModalBody>
        <p css={Css.mb3.$}>Add a new level and rooms on that level. Or, add rooms to an existing level.</p>
        <FormLines labelStyle="above" width="full">
          <BoundSelectField field={formState.levelId} label="Level" options={levelOptions} />
          <BoundSelectField field={formState.roomTypeId} label="Room" options={roomTypes} />
        </FormLines>
      </ModalBody>
      <ModalFooter>
        <Button variant="quaternary" label="Cancel" onClick={closeModal} />
        <Button label="Save" onClick={onSave} disabled={disabledReason} />
      </ModalFooter>
    </>
  );
}

type AddLocationForm = Pick<SaveReadyPlanLocationInput, "levelId" | "roomTypeId">;

const addLocationConfig: ObjectConfig<AddLocationForm> = {
  levelId: { type: "value", rules: [required] },
  roomTypeId: { type: "value" },
};
